import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { CapTablesService } from 'api';
import { SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS } from 'common/constants/services';
import { CAP_TABLE_BY_ID_DEFAULT_ERROR_MESSAGE, CAP_TABLE_BY_ID_QUERY_KEY } from 'common/constants/services/capTables';
import { throwError } from 'utilities';
import {
  GetCapTableByIdHook,
  GetCapTableByIdParams,
  GetCapTableByIdResponse,
  UseGetCapTableById,
} from './types';

const getCapTableById = async (params: GetCapTableByIdParams): Promise<GetCapTableByIdResponse | null> => {
  const { capTableId } = params;

  try {
    const capTable = await CapTablesService.apiCapTablesRead(capTableId);
    if (!capTable) {
      return throwError(CAP_TABLE_BY_ID_DEFAULT_ERROR_MESSAGE);
    }
    return capTable;
  } catch (error) {
    return throwError(CAP_TABLE_BY_ID_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetCapTableById: UseGetCapTableById = params => {
  const { capTableId, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetching, isLoading, refetch } = useQuery<GetCapTableByIdHook['data'], Error>({
    enabled: !isNil(capTableId) && shouldQueryAutomatically,
    queryFn: () => getCapTableById({ capTableId: capTableId as number }),
    queryKey: [CAP_TABLE_BY_ID_QUERY_KEY, capTableId],
    refetchOnWindowFocus: SERVICES_VALUATIONS_REFETCH_ON_WINDOW_FOCUS,
  });

  return {
    data,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetCapTableById;
